import React, { useState, useEffect } from 'react'
import { Search, Check, Filter } from 'lucide-react'
import {
  Button, Badge, Card, CardContent, Input, Label, ChipFilter,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../../../ui'
import { asignarPrenda, actualizarAsignacion } from '../../../../lib/supabase'
import { useConfirmDialog } from '../../../../hooks/useConfirmDialog'
import { useDeportes } from '../../../../hooks/useDeportes'
import { etiquetaTemporada } from '../../../../lib/uniformesMultiTemporada'
import { estadoDePago } from '../../../../lib/uniformesEstadoPago'
import { jugadorEnGrupo, aggregateAvance } from './entregaHelpers'
import { ctxDeEntrega, etapaUniforme, puedeEntregar, numeroEfectivo, ETAPA_LABEL } from './uniformesControlHelpers'
import TablaPorJugador from './TablaPorJugador'

// `conEntrega` parte esta pantalla en sus dos trabajos, que estan separados por
// semanas y por el proveedor: capturar la talla (Tallas) y repartir lo que llego
// (Reparto). Juntos en una sola entrada del menu, nadie sabia cual de los dos venia
// a hacer.
const EstacionEntrega = ({ matriz, grupos, temporadas = [], temporadaId, onTemporadaChange, prendaIdInicial, onChanged, entregasDelPadron, conEntrega = true, conTemporada = true }) => {
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const { label: deporteLabel } = useDeportes()

  // Dos ejes para el mismo trabajo: 'prenda' para repartir una caja (una fila por
  // jugador, con su boton de Entregar); 'jugador' para el dia que se mide al equipo
  // (una fila por jugador con TODAS sus prendas, y se le llama una sola vez).
  const [eje, setEje] = useState('prenda')
  const [prendaId, setPrendaId] = useState(prendaIdInicial || '')
  const [grupoSel, setGrupoSel] = useState([])
  const [soloPendientes, setSoloPendientes] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [savingIds, setSavingIds] = useState(new Set())
  const [grupoChoice, setGrupoChoice] = useState({})

  const jugadorById = Object.fromEntries((matriz.jugadores || []).map((j) => [j.id, j]))
  const prendaById = Object.fromEntries((matriz.prendas || []).map((p) => [p.id, p]))
  const prenda = prendaById[prendaId] || null

  // Sin prenda elegida la vista por prenda no muestra nada. Solo llega prendaIdInicial
  // cuando se entra desde el tablero tocando una prenda; por el menu llegaba vacia.
  //
  // Tambien reencuadra al cambiar de temporada: las prendas de FLAG no son las de
  // TACKLE, y quedarse en una que la nueva temporada no lleva dejaba la lista vacia
  // sin explicar por que.
  useEffect(() => {
    const disponibles = (matriz.prendas || []).filter(
      (p) => (entregasDelPadron || matriz.entregas || []).some((e) => e.prenda_id === p.id),
    )
    if (prendaId && disponibles.some((p) => p.id === prendaId)) return
    const primera = disponibles[0] || (matriz.prendas || [])[0]
    if (primera && primera.id !== prendaId) setPrendaId(primera.id)
  }, [matriz.prendas, matriz.entregas, entregasDelPadron, prendaId])

  const setSaving = (id, on) => setSavingIds((prev) => {
    const next = new Set(prev)
    if (on) next.add(id); else next.delete(id)
    return next
  })

  // Las opciones salen de lo que HAY, no de un catalogo fijo: un chip de un grupo
  // sin gente solo estorba.
  const todasLasEntregas = entregasDelPadron || matriz.entregas || []
  const gruposConGente = (grupos || [])
    .filter((g) => (matriz.jugadores || []).some((j) => jugadorEnGrupo(j, g.id)))
    .map((g) => ({ value: g.id, label: g.nombre_del_grupo }))
  const chipsPrenda = (matriz.prendas || [])
    .filter((p) => todasLasEntregas.some((e) => e.prenda_id === p.id))
    .map((p) => ({ value: p.id, label: p.nombre }))

  // Se ofrecen las ABIERTAS mas la que este puesta, aunque ya se haya cerrado: si no,
  // quien llega con una temporada vieja guardada no tiene con que salirse de ella.
  const chipsTemporada = (temporadas || [])
    .filter((t) => t.estado === 'abierta' || t.id === temporadaId)
    .map((t) => ({ value: t.id, label: etiquetaTemporada(t, deporteLabel) }))

  // Grupo, busqueda y estado aplican a los DOS ejes. Antes vivian encadenados
  // despues del filtro por prenda, que dejaba a la vista por jugador con una sola
  // columna.
  const termino = searchTerm.trim().toLowerCase()
  const visibles = todasLasEntregas.filter((e) => {
    const jugador = jugadorById[e.jugador_id]
    if (!jugador) return false
    if (grupoSel.length && !grupoSel.some((gid) => jugadorEnGrupo(jugador, gid))) return false
    if (soloPendientes && e.estatus === 'entregado') return false
    if (termino) {
      const nombre = (jugador.nombre_completo || '').toLowerCase()
      const nombrePrenda = (prendaById[e.prenda_id]?.nombre || '').toLowerCase()
      if (!nombre.includes(termino) && !nombrePrenda.includes(termino) && !String(jugador.numero || '').includes(termino)) return false
    }
    return true
  })

  const rows = visibles
    .filter((e) => e.prenda_id === prendaId)
    .map((e) => ({ entrega: e, jugador: jugadorById[e.jugador_id] }))
    .sort((a, b) => (a.jugador.nombre_completo || '').localeCompare(b.jugador.nombre_completo || ''))

  const avance = aggregateAvance(rows.map((r) => r.entrega))

  // Lo que llego de esta prenda y cuanto queda libre. Se suma por LINEA del pedido
  // (una linea = prenda + talla), que es donde vive `cantidad_recibida`. Solo tiene
  // sentido en la prenda intercambiable: en la personalizada cada pieza es de su
  // dueno y "quedan 5" no querria decir nada.
  const existencia = (() => {
    if (!prenda || prenda.personalizada !== false) return null
    const porLinea = new Map()
    rows.forEach(({ entrega }) => {
      if (!entrega.linea?.id) return
      if (!porLinea.has(entrega.linea.id)) {
        porLinea.set(entrega.linea.id, {
          recibidas: entrega.linea.cantidad_recibida || 0,
          entregadas: entrega.entregados_de_linea || 0,
        })
      }
    })
    if (!porLinea.size) return null
    const recibidas = [...porLinea.values()].reduce((s, l) => s + l.recibidas, 0)
    const entregadas = [...porLinea.values()].reduce((s, l) => s + l.entregadas, 0)
    return { recibidas, entregadas, libres: Math.max(0, recibidas - entregadas) }
  })()
  const hayFiltrosActivos = grupoSel.length > 0 || soloPendientes
  const conteo = eje === 'prenda' ? rows.length : new Set(visibles.map((e) => e.jugador_id)).size

  // Grupos candidatos para la prenda actual: grupos activos del jugador cuyo
  // deporte coincide con el de la prenda (o la prenda aplica a "todos").
  const candidatosGrupo = (jugador, laPrenda) => (jugador?.jugadores_grupos || []).filter(
    (jg) => jg.activo && !jg.deleted && jg.grupos && (jg.grupos.deporte === laPrenda?.deporte || laPrenda?.deporte === 'todos')
  )

  // 1 candidato -> automatico. Varios -> lo que haya elegido el usuario en el
  // selector de esa fila. 0 -> null (asignarPrenda hace fallback al numero del club).
  const resolverGrupoId = (entrega, jugador) => {
    const candidatos = candidatosGrupo(jugador, prendaById[entrega.prenda_id])
    if (candidatos.length === 1) return candidatos[0].grupo_id
    if (candidatos.length > 1) return grupoChoice[entrega.id] || null
    return null
  }

  const persist = async (entrega, { talla, estatus }, grupoId) => {
    if (entrega.asignacionId) {
      return actualizarAsignacion(entrega.asignacionId, { talla, estatus })
    }
    return asignarPrenda({ jugadorId: entrega.jugador_id, prendaId: entrega.prenda_id, talla, temporadaId, estatus, origen: 'plan', grupoId })
  }

  const setTalla = async (entrega, jugador, t) => {
    if (savingIds.has(entrega.id)) return   // el segundo clic se ignora, no se encola
    const estatus = entrega.estatus === 'entregado' ? 'entregado' : 'pendiente'
    setSaving(entrega.id, true)
    const { error } = await persist(entrega, { talla: t, estatus }, resolverGrupoId(entrega, jugador))
    setSaving(entrega.id, false)
    if (error) { await alert('Error', error.message || 'No se pudo guardar la talla'); return }
    onChanged()
  }

  const entregar = async (entrega, jugador) => {
    if (!entrega.talla) { await alert('Falta talla', 'Selecciona una talla antes de entregar.'); return }
    if (!puedeEntregar(entrega, ctxDeEntrega(entrega))) {
      await alert('Aún no disponible', entrega.personalizada === false
        ? 'No quedan piezas recibidas de esta talla. Registra la recepción en el pedido.'
        : 'Esta prenda aún no se ha recibido del proveedor. Regístrala como recibida en el pedido.')
      return
    }
    if (!entrega.pagado) {
      const ok = await confirm('Aún no cubre lo requerido', '¿Entregar de todos modos?')
      if (!ok) return
    }
    setSaving(entrega.id, true)
    const { error } = await persist(entrega, { talla: entrega.talla, estatus: 'entregado' }, resolverGrupoId(entrega, jugador))
    setSaving(entrega.id, false)
    if (error) { await alert('Error', error.message || 'No se pudo registrar la entrega'); return }
    onChanged()
  }

  const revertir = async (entrega, jugador) => {
    setSaving(entrega.id, true)
    const { error } = await persist(entrega, { talla: entrega.talla, estatus: 'pendiente' }, resolverGrupoId(entrega, jugador))
    setSaving(entrega.id, false)
    if (error) { await alert('Error', error.message || 'No se pudo actualizar'); return }
    onChanged()
  }

  // Los botones de talla y la accion de entregar, compartidos por la tarjeta de
  // movil y el renglon de escritorio: una sola verdad para las dos anchuras.
  // aria-pressed no es adorno: la talla elegida se distinguia SOLO por el color del
  // boton. Quien no lo distingue -- o usa lector de pantalla -- oia cinco botones
  // identicos y no sabia cual estaba puesta.
  const BotonesTalla = ({ r, grupoPendiente }) => {
    const e = r.entrega
    const tallas = prenda?.tallas || []
    if (!tallas.length) return <span className="text-xs text-muted-foreground">Sin tallas configuradas</span>
    return (
      <div className="flex flex-wrap items-center gap-1">
        {tallas.map((t) => (
          <Button
            key={t}
            size="sm"
            variant={e.talla === t ? 'default' : 'outline'}
            className="h-7 min-w-9 px-2 text-xs"
            aria-pressed={e.talla === t}
            aria-label={`Talla ${t} para ${r.jugador?.nombre_completo || 'el jugador'}`}
            disabled={savingIds.has(e.id) || grupoPendiente}
            onClick={() => setTalla(e, r.jugador, t)}
          >
            {t}
          </Button>
        ))}
        {savingIds.has(e.id) && <span className="text-[11px] text-muted-foreground">Guardando...</span>}
      </div>
    )
  }

  const AccionEntrega = ({ r, grupoPendiente }) => {
    const e = r.entrega
    const saving = savingIds.has(e.id)
    const ctx = ctxDeEntrega(e)
    const etapa = etapaUniforme(e, ctx)
    const enTransito = e.estatus !== 'entregado' && e.talla && ['por_pedir', 'en_pedido', 'pedido'].includes(etapa)
    if (e.estatus === 'entregado') {
      return (
        <Button variant="outline" size="sm" disabled={saving} onClick={() => revertir(e, r.jugador)}>
          <Check className="mr-1 h-4 w-4 text-green-600" /> Entregado
        </Button>
      )
    }
    return (
      <div className="flex flex-col items-end gap-1">
        <Button size="sm" disabled={saving || !e.talla || !puedeEntregar(e, ctx) || grupoPendiente} onClick={() => entregar(e, r.jugador)}>
          {saving ? 'Guardando...' : 'Entregar'}
        </Button>
        {enTransito && (
          <span className="text-[10px] text-muted-foreground">
            {etapa === 'pedido' ? `${ETAPA_LABEL.pedido} — por recibir` : ETAPA_LABEL[etapa]}
          </span>
        )}
      </div>
    )
  }

  const SelectorGrupo = ({ e, candidatos }) => (
    <Select value={grupoChoice[e.id] || ''} onValueChange={(v) => setGrupoChoice((prev) => ({ ...prev, [e.id]: v }))}>
      <SelectTrigger className="h-7 w-48 text-xs" aria-label="Grupo del jugador">
        <SelectValue placeholder="Elige el grupo..." />
      </SelectTrigger>
      <SelectContent>
        {candidatos.map((c) => <SelectItem key={c.grupo_id} value={c.grupo_id}>{c.grupos?.nombre_del_grupo}</SelectItem>)}
      </SelectContent>
    </Select>
  )

  const datosDeFila = (r) => {
    const candidatos = candidatosGrupo(r.jugador, prenda)
    return {
      candidatos,
      // Solo bloquea mientras la fila no tiene registro: una vez creada, su grupo_id
      // ya quedo fijo y no hace falta volver a elegir.
      grupoPendiente: !r.entrega.asignacionId && candidatos.length > 1 && !grupoChoice[r.entrega.id],
    }
  }

  return (
    <div>
      {/* La temporada, primero que nada. Esta pantalla NUNCA la mostro: trabajaba con
          la que quedara guardada en localStorage desde la pantalla de Entregas. Con
          FLAG y TACKLE abiertas a la vez se podian capturar tallas en la temporada
          equivocada sin manera de darse cuenta. */}
      {conTemporada && chipsTemporada.length > 0 && (
        <div className="mb-3 space-y-1.5">
          <Label className="text-xs text-muted-foreground">Temporada</Label>
          <ChipFilter
            ariaLabel="Temporada"
            scroll
            opciones={chipsTemporada}
            valor={temporadaId || ''}
            onChange={(v) => v && onTemporadaChange?.(v)}
          />
        </div>
      )}

      {/* El eje: cambia de que va la pantalla. */}
      <div className="mb-3 space-y-1.5">
        <Label className="text-xs text-muted-foreground">Cómo capturar</Label>
        <ChipFilter
          ariaLabel="Cómo capturar"
          opciones={[
            { value: 'prenda', label: 'Por prenda' },
            { value: 'jugador', label: 'Por jugador' },
          ]}
          valor={eje}
          onChange={(v) => v && setEje(v)}
        />
      </div>

      {/* La prenda como chips y no como desplegable: son tres o cuatro por deporte,
          y un select las esconde a todas detras de un clic (ux-rules §13). */}
      {eje === 'prenda' && chipsPrenda.length > 0 && (
        <div className="mb-3 space-y-1.5">
          <Label className="text-xs text-muted-foreground">Prenda</Label>
          <ChipFilter
            ariaLabel="Prenda"
            scroll
            opciones={chipsPrenda}
            valor={prendaId}
            onChange={(v) => v && setPrendaId(v)}
          />
        </div>
      )}

      {/* Barra estandar del proyecto: buscador que crece, conteo, Filtros. Aqui no
          hay accion primaria -- en esta pantalla no se crea nada, se captura. */}
      <div className="mb-4 flex flex-nowrap items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar jugador..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <span className="hidden whitespace-nowrap text-sm text-muted-foreground sm:block">
          <span className="font-medium text-foreground tabular-nums">{conteo}</span>
          {eje === 'prenda' ? ' jugadores' : ' por medir'}
        </span>
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="relative h-10 w-10 shrink-0 p-0 sm:h-10 sm:w-auto sm:px-3"
        >
          <Filter className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Filtros</span>
          {hayFiltrosActivos && <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary" />}
        </Button>
      </div>

      {showFilters && (
        <Card className="mb-4">
          <CardContent className="space-y-4 pt-4">
            {gruposConGente.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs">Grupo</Label>
                <ChipFilter ariaLabel="Grupo" multiple opciones={gruposConGente} valor={grupoSel} onChange={setGrupoSel} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Ver</Label>
              <ChipFilter
                ariaLabel="Ver"
                opciones={[
                  { value: 'todos', label: 'Todos' },
                  { value: 'pendientes', label: 'Solo los que faltan' },
                ]}
                valor={soloPendientes ? 'pendientes' : 'todos'}
                onChange={(v) => v && setSoloPendientes(v === 'pendientes')}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* La cuenta de la caja. Existia en el codigo -- decide si el boton "Entregar"
          se habilita -- pero no se veia: el boton salia apagado y habia que adivinar
          por que. Solo aplica a la prenda INTERCAMBIABLE: la personalizada lleva
          numero bordado y es de su dueno, no de quien llegue primero. */}
      {eje === 'prenda' && conEntrega && prenda && existencia && (
        <p className="mb-3 rounded-md border bg-muted/40 px-3 py-2 text-xs">
          De esta caja llegaron <strong className="tabular-nums">{existencia.recibidas}</strong>,
          se entregaron <strong className="tabular-nums">{existencia.entregadas}</strong> y
          quedan <strong className={`tabular-nums ${existencia.libres > 0 ? 'text-green-700' : 'text-amber-700'}`}>{existencia.libres}</strong> por repartir.
        </p>
      )}

      {eje === 'prenda' && prenda && (
        <div className="mb-4 flex flex-wrap gap-4 text-sm text-muted-foreground">
          {conEntrega && (
            <>
              <span className="tabular-nums">{avance.pendientes} {avance.pendientes === 1 ? 'pendiente' : 'pendientes'}</span>
              <span className="tabular-nums text-green-600">{avance.entregados} {avance.entregados === 1 ? 'entregado' : 'entregados'}</span>
            </>
          )}
          <span className="tabular-nums">{avance.sinTalla} sin talla</span>
        </div>
      )}

      {eje === 'jugador' ? (
        <TablaPorJugador
          entregas={visibles}
          jugadorById={jugadorById}
          prendas={matriz.prendas || []}
          guardando={savingIds}
          onCapturarTalla={setTalla}
        />
      ) : !prenda ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Elige una prenda para empezar a registrar entregas.</CardContent></Card>
      ) : rows.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Ningún jugador necesita esta prenda con los filtros actuales.</CardContent></Card>
      ) : (
        <>
          {/* Movil: una tarjeta por jugador. La tabla de tres columnas se recortaba
              en silencio y el boton "Entregar" desaparecia -- justo en la pantalla
              que se usa en el campo, con el telefono (ux-rules §11). */}
          <div className="space-y-3 sm:hidden">
            {rows.map((r) => {
              const e = r.entrega
              const { candidatos, grupoPendiente } = datosDeFila(r)
              const numero = numeroEfectivo(e, r.jugador)
              const pago = estadoDePago(e)
              return (
                <Card key={e.id}>
                  <CardContent className="space-y-2 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="min-w-0 flex-1 truncate font-medium">{r.jugador.nombre_completo}</span>
                      {numero != null && <Badge variant="outline" className="text-[10px]">#{numero}</Badge>}
                      {e.origen !== 'plan' && <Badge variant="outline">Extra</Badge>}
                    </div>
                    <Badge variant={pago.variant}>{pago.label}</Badge>
                    {candidatos.length > 1 && !e.asignacionId && <SelectorGrupo e={e} candidatos={candidatos} />}
                    <BotonesTalla r={r} grupoPendiente={grupoPendiente} />
                    {conEntrega && <div className="flex justify-end"><AccionEntrega r={r} grupoPendiente={grupoPendiente} /></div>}
                  </CardContent>
                </Card>
              )
            })}
          </div>

          {/* Escritorio: la tabla completa */}
          <Card className="hidden sm:block">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Jugador</TableHead>
                    <TableHead>Talla</TableHead>
                    {conEntrega && <TableHead className="text-right">Acción</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const e = r.entrega
                    const { candidatos, grupoPendiente } = datosDeFila(r)
                    const numero = numeroEfectivo(e, r.jugador)
                    const pago = estadoDePago(e)
                    return (
                      <TableRow key={e.id}>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{r.jugador.nombre_completo}</span>
                            {numero != null && <Badge variant="outline" className="text-[10px]">#{numero}</Badge>}
                            {e.origen !== 'plan' && <Badge variant="outline">Extra</Badge>}
                            <Badge variant={pago.variant}>{pago.label}</Badge>
                          </div>
                          {candidatos.length > 1 && !e.asignacionId && (
                            <div className="mt-1.5"><SelectorGrupo e={e} candidatos={candidatos} /></div>
                          )}
                        </TableCell>
                        <TableCell><BotonesTalla r={r} grupoPendiente={grupoPendiente} /></TableCell>
                        {conEntrega && <TableCell className="text-right"><AccionEntrega r={r} grupoPendiente={grupoPendiente} /></TableCell>}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}

      <ConfirmDialog />
    </div>
  )
}

export default EstacionEntrega
